import { num, resolveColor, str } from './settingValue.js';
/**
 * The inspector fields for the background-PATTERN engine (Shrine's "Background pattern" group), shared so
 * every section that supports a pattern declares the identical control set. Spread into a schema's fields.
 */
export function patternFields(group = 'Background pattern') {
    return [
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group,
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group,
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group,
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group,
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group,
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group,
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
    ];
}
/** Read a section's background-pattern settings (the `pattern*` ids) into a resolved config. */
export function readPattern(s) {
    const mode = str(s.patternOpacityMode, 'low');
    const opacityPct = mode === 'normal' ? num(s.patternOpacityNormal, 80) : num(s.patternOpacityLow, 7);
    return {
        pattern: str(s.patternBg, 'none'),
        color: resolveColor(s.patternColor, 'var(--aqx-theme-accent)'),
        size: num(s.patternSize, 20),
        thickness: num(s.patternThickness, 2),
        angle: num(s.patternAngle, 45),
        opacity: opacityPct / 100,
    };
}
/** The pattern's `background*` declarations (ported verbatim from Shrine's pattern-background snippet). */
export function patternLayerCss(c) {
    const { color, size, thickness, angle } = c;
    switch (c.pattern) {
        case 'dotted':
            return `background-image:radial-gradient(${color} ${thickness}px,transparent ${thickness}px);background-size:${size}px ${size}px;`;
        case 'grid':
            return `background-image:linear-gradient(to right,${color} ${thickness}px,transparent ${thickness}px),linear-gradient(to bottom,${color} ${thickness}px,transparent ${thickness}px);background-size:${size}px ${size}px;`;
        case 'curved-grid':
            return `--cg-size:${size * 5}px;--cg-color:${color};--cg-shape:transparent 52%,var(--cg-color) 54% 57%,transparent 59%;background:radial-gradient(farthest-side at -33.33% 50%,var(--cg-shape)) 0 calc(var(--cg-size)/2),radial-gradient(farthest-side at 50% 133.33%,var(--cg-shape)) calc(var(--cg-size)/2) 0,radial-gradient(farthest-side at 133.33% 50%,var(--cg-shape)),radial-gradient(farthest-side at 50% -33.33%,var(--cg-shape));background-size:calc(var(--cg-size)/4.667) var(--cg-size),var(--cg-size) calc(var(--cg-size)/4.667);background-repeat:repeat;`;
        case 'diagonal-stripes':
            return `background-image:repeating-linear-gradient(${angle}deg,${color} 0,${color} ${thickness}px,transparent ${thickness}px,transparent ${size}px);`;
        case 'zigzag':
            return `background-image:linear-gradient(135deg,${color} 25%,transparent 25%),linear-gradient(225deg,${color} 25%,transparent 25%),linear-gradient(45deg,${color} 25%,transparent 25%),linear-gradient(315deg,${color} 25%,transparent 25%);background-position:${size / 2}px 0,${size / 2}px 0,0 0,0 0;background-size:${size}px ${size}px;`;
        case 'arrow-pattern': {
            const c33 = `color-mix(in srgb,${color} 33%,transparent)`;
            return `background-image:linear-gradient(135deg,${c33} 25%,transparent 25%),linear-gradient(225deg,${color} 25%,transparent 25%),linear-gradient(315deg,${c33} 25%,transparent 25%),linear-gradient(45deg,${color} 25%,transparent 25%);background-position:${-size * 0.5}px 0,${-size * 0.5}px 0,0 0,0 0;background-size:${size}px ${size}px;`;
        }
        case 'radial-rings':
            return `background-image:repeating-radial-gradient(circle at center,${color} 0,${color} ${thickness}px,transparent ${thickness}px,transparent ${size}px);`;
        case 'diamond-grid-1':
            return `background-image:linear-gradient(135deg,${color} 25%,transparent 25%),linear-gradient(225deg,${color} 25%,transparent 25%),linear-gradient(45deg,${color} 25%,transparent 25%),linear-gradient(315deg,${color} 25%,transparent 25%);background-position:${size}px 0,${size}px 0,0 0,0 0;background-size:${size}px ${size}px;`;
        case 'diamond-grid-2':
            return `background-image:linear-gradient(135deg,transparent 25%,${color} 25%,${color} 50%,transparent 50%,transparent 75%,${color} 75%,${color} 100%),linear-gradient(225deg,transparent 25%,${color} 25%,${color} 50%,transparent 50%,transparent 75%,${color} 75%,${color} 100%);background-size:${size}px ${size}px;background-position:0 0,${size / 2}px ${size / 2}px;`;
        case 'quatrefoils':
            return `--qf-size:${size * 1.2}px;--qf-color:${color};--qf-shape:transparent 83%,var(--qf-color) 85% 99%,transparent 101%;background:radial-gradient(27% 29% at right,var(--qf-shape)) calc(var(--qf-size)/2) var(--qf-size),radial-gradient(27% 29% at left,var(--qf-shape)) calc(var(--qf-size)/-2) var(--qf-size),radial-gradient(29% 27% at top,var(--qf-shape)) 0 calc(var(--qf-size)/2),radial-gradient(29% 27% at bottom,var(--qf-shape)) 0 calc(var(--qf-size)/-2);background-size:calc(2*var(--qf-size)) calc(2*var(--qf-size));background-repeat:repeat;`;
        case 'ovals':
            return `--ov-size:${size * 0.5}px;--ov-color:${color};--ov-g:transparent calc(100% - var(--ov-size)/3 - 1px),var(--ov-color) calc(100% - var(--ov-size)/3) calc(100% - 1px),transparent;--ov-r:calc(2.414*var(--ov-size));--ov-p:calc(1.414*var(--ov-size));background:radial-gradient(var(--ov-r) at 0 0,var(--ov-g)) var(--ov-r) var(--ov-r),radial-gradient(var(--ov-size) at 0 0,var(--ov-g)) var(--ov-p) var(--ov-p),radial-gradient(var(--ov-r) at 0 100%,var(--ov-g)) 0 var(--ov-p),radial-gradient(var(--ov-size) at 0 100%,var(--ov-g)) calc(-1*var(--ov-size)) var(--ov-r),radial-gradient(var(--ov-r) at 100% 0,var(--ov-g)) var(--ov-p) 0,radial-gradient(var(--ov-size) at 100% 0,var(--ov-g)) var(--ov-r) calc(-1*var(--ov-size)),radial-gradient(var(--ov-r) at 100% 100%,var(--ov-g)) calc(-1*var(--ov-size)) calc(-1*var(--ov-size)),radial-gradient(var(--ov-size) at 100% 100%,var(--ov-g));background-size:calc(2*var(--ov-r)) calc(2*var(--ov-r));background-repeat:repeat;`;
        case 'hexagons':
            return `--hx-size:${size}px;--hx-color:${color};--hx-c:transparent,var(--hx-color) .5deg 119.5deg,transparent 120deg;--hx-g1:conic-gradient(from 60deg at 56.25% calc(425%/6),var(--hx-c));--hx-g2:conic-gradient(from 180deg at 43.75% calc(425%/6),var(--hx-c));--hx-g3:conic-gradient(from -60deg at 50% calc(175%/12),var(--hx-c));background:var(--hx-g1),var(--hx-g1) var(--hx-size) calc(1.73*var(--hx-size)),var(--hx-g2),var(--hx-g2) var(--hx-size) calc(1.73*var(--hx-size)),var(--hx-g3) var(--hx-size) 0,var(--hx-g3) 0 calc(1.73*var(--hx-size));background-size:calc(2*var(--hx-size)) calc(3.46*var(--hx-size));background-repeat:repeat;`;
        case 'curvy':
            return `--curvy-size:${size}px;--curvy-color:${color};--curvy-s:var(--curvy-size);--curvy-space:calc(2*var(--curvy-s));--curvy-g:35.36% 35.36% at;--curvy-shape:transparent 66%,var(--curvy-color) 68% 70%,transparent 72%;background:radial-gradient(var(--curvy-g) 100% 25%,var(--curvy-shape)) var(--curvy-s) var(--curvy-s)/var(--curvy-space) var(--curvy-space),radial-gradient(var(--curvy-g) 0 75%,var(--curvy-shape)) var(--curvy-s) var(--curvy-s)/var(--curvy-space) var(--curvy-space),radial-gradient(var(--curvy-g) 100% 25%,var(--curvy-shape)) 0 0/var(--curvy-space) var(--curvy-space),radial-gradient(var(--curvy-g) 0 75%,var(--curvy-shape)) 0 0/var(--curvy-space) var(--curvy-space);background-repeat:repeat;`;
        default:
            return '';
    }
}
/**
 * The full `::before` pattern overlay rule for a section, or '' when no pattern is set. `sectionSel` is
 * the section's CSS selector (e.g. `.aqx-fc-xxx__sec` or `.aqx-hero-xxx`); its direct children are lifted
 * above the overlay via a z-index rule.
 */
export function patternCss(sectionSel, c) {
    if (c.pattern === 'none')
        return '';
    return (`${sectionSel}{position:relative;isolation:isolate}` +
        `${sectionSel}::before{content:'';position:absolute;inset:0;background-repeat:repeat;background-position:0 0;opacity:${c.opacity};pointer-events:none;z-index:0;${patternLayerCss(c)}}` +
        `${sectionSel}>*{position:relative;z-index:1}`);
}
//# sourceMappingURL=pattern.js.map